import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Inbox, Wallet, Star, ShieldCheck, Clock, TrendingUp } from "lucide-react";
import { IconGift, IconInfinity, IconReceipt } from "@tabler/icons-react";
import PublicLayout, { PageHero } from "../../components/PublicLayout";
import { useLang } from "../../context/LanguageContext";
import { useDashT } from "../../lib/dashboardStrings";
import { CREDIT_PRICING } from "../../lib/platformConstants";

const icons = [Inbox, Wallet, Star, ShieldCheck, Clock, TrendingUp];

const ForTalents = () => {
  const { t, lang, setLang } = useLang();
  const dt = useDashT();
  const { pathname } = useLocation();
  // /sv/... URL entry should switch the app language to Swedish so the rest
  // of the page (which reads `dt()`) renders Swedish. Anything else and the
  // app-language state is the source of truth.
  React.useEffect(() => {
    if (pathname.startsWith("/sv") && lang !== "sv") setLang("sv");
  }, [pathname, lang, setLang]);
  const points = t("pages.tal.points");

  const talent = CREDIT_PRICING.talent;
  // Only the "Talent" price of 99 kr is displayed publicly per Brief 6.
  // Underlying CREDIT_PRICING data is retained for existing purchase flows.

  const trust = [
    { Icon: IconGift, color: "#1A8A4A", text: dt("3 free credits when you complete your profile") },
    { Icon: IconInfinity, color: "#FF5C00", text: dt("Credits never expire") },
    { Icon: IconReceipt, color: "rgba(245,243,238,0.7)", text: dt("Full VAT invoice on every purchase") },
  ];

  return (
    <PublicLayout>
      <PageHero eyebrow={t("pages.tal.eyebrow")} title={t("pages.tal.title")} subtitle={t("pages.tal.subtitle")} />
      <section className="max-w-[1100px] mx-auto px-6 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {points.map((p, i) => {
            const Icon = icons[i];
            return (
              <div key={p.title} className="rounded-2xl border border-[#252525] bg-[#1A1A1A] p-7 hover:border-[#FF5C00]/40 transition-colors">
                <div className="w-11 h-11 rounded-xl bg-[#FF5C00]/12 flex items-center justify-center"><Icon size={20} className="text-[#FF5C00]" /></div>
                <h3 className="mt-6 text-[18px] font-semibold text-[#F5F3EE]">{p.title}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-neutral-400">{p.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Welcome bonus banner (§10) */}
      <div className="max-w-[900px] mx-auto px-6 pb-4">
        <div
          className="rounded-2xl border border-[#1A8A4A]/40 bg-[#0F1F14] p-4 flex items-center gap-3"
          data-testid="tal-welcome-bonus-banner"
        >
          <IconGift size={22} className="text-[#1A8A4A] shrink-0" stroke={1.7} />
          <div className="text-[14px] leading-snug text-[#F5F3EE]">
            <b>{dt("Welcome bonus:")}</b>{" "}
            {dt("3 free unlocks worth 297 kr when you complete your profile.")}
          </div>
        </div>
      </div>

      {/* Pricing section (§5) */}
      <section id="pricing" className="max-w-[900px] mx-auto px-6 pb-20" data-testid="tal-pricing-section">
        <div className="text-center">
          <h2 className="text-[30px] font-bold text-[#F5F3EE]">{dt("Simple, transparent pricing")}</h2>
          <p className="mt-3 text-[16px] text-neutral-400 max-w-[600px] mx-auto">
            {dt("One flat price per unlock. No packages, no monthly fees. Keep what you invoice, minus a transparent 10% platform fee shown in every offer.")}
          </p>
        </div>

        <div className="mt-10 max-w-[520px] mx-auto">
          <div className="rounded-2xl border border-[#252525] bg-[#1A1A1A] p-7 text-center" data-testid="tal-price-unified">
            <div className="text-[13px] font-semibold uppercase tracking-wide text-neutral-500">{dt("Per unlock")}</div>
            <div className="mt-3 text-[42px] font-bold text-[#F5F3EE] tracking-tight">99 {dt("SEK")}</div>
            <div className="text-[14px] text-neutral-400">{dt("Same price for talents and agencies. Incl. VAT.")}</div>
            <div className="mt-4 pt-4 border-t border-[#252525] text-[13.5px] text-neutral-500">
              {dt("Take a 10-pack for 799 kr and save nearly 20%.")}
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row gap-4">
          {trust.map((b) => (
            <div key={b.text} className="flex items-center gap-2.5 text-[14px] text-[#F5F3EE]/90 flex-1">
              <b.Icon size={22} style={{ color: b.color }} stroke={1.7} />
              <span>{b.text}</span>
            </div>
          ))}
        </div>

        <div className="mt-8 text-center">
          <Link to="/register" data-testid="tal-pricing-cta" className="inline-flex items-center gap-2 bg-[#1A8A4A] hover:bg-[#20A458] text-white font-semibold px-7 py-3.5 rounded-xl transition-colors">
            {dt("Create your free profile")} <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <section className="max-w-[820px] mx-auto px-6 pb-24 text-center">
        <h2 className="text-[30px] font-bold text-[#F5F3EE]">{t("pages.tal.ctaTitle")}</h2>
        <p className="text-neutral-400 mt-3">{t("pages.tal.ctaText")}</p>
        <Link to="/signin" className="inline-flex items-center gap-2 mt-7 bg-[#1A8A4A] hover:bg-[#20A458] text-white font-semibold px-7 py-3.5 rounded-xl transition-colors">{t("pages.tal.cta")} <ArrowRight size={18} /></Link>
      </section>
    </PublicLayout>
  );
};

export default ForTalents;
